<script setup>
import AutoComplete from 'primevue/autocomplete';
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    options: {
        type: Array,
        default: () => []
    },
    visible: {
        type: Boolean,
        default: true
    },
    width: {
        type: String,
        default: '12.5rem'
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['update:modelValue', 'input', 'enter', 'item-select', 'update-options']);
const selectWidth = computed(() => props.width || '12.5rem');
const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const items = ref([]);
const autocompleteRef = ref(null);
const selectedValue = ref(props.modelValue);
const errMessage = ref(props.errmsg);
const ValidationMsg = ref('');

const updateItems = (options) => {
    if (Array.isArray(options)) {
        items.value = options.map((option) => ({
            value: option.value.toString(),
            label: option.disptext && option.disptext !== '' ? option.disptext : option.text
        }));
    } else {
        items.value = [];
    }
};

watch(
    () => props.options,
    (newOptions) => {
        updateItems(newOptions || []);
    },
    { immediate: true }
);

watch(
    () => props.modelValue,
    (newValue) => {
        const selectedOption = items.value.find((option) => option.value === newValue.toString());
        inputValue.value = selectedOption ? selectedOption.label : newValue.toString();
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const search = (event) => {
    console.log(event);
    const query = event.query.toString().toLowerCase() === '' ? inputValue.value.toString().toLowerCase() : event.query.toString().toLowerCase();
    if (query === '') {
        items.value = props.options.map((option) => ({
            value: option.value,
            label: option.disptext && option.disptext !== '' ? option.disptext : option.text
        }));
        return;
    }

    // Filter items based on the query (both label and value)
    items.value = props.options
        .map((option) => ({
            value: option.value,
            label: option.disptext && option.disptext !== '' ? option.disptext : option.text
        }))
        .filter((option) => option.label.toLowerCase().includes(query) || option.value.toString().toLowerCase().includes(query));
};

const onInput = (event) => {
    ValidationMsg.value = '';
    const value = event.target.value;
    inputValue.value = value;
    emit('update:modelValue', value);
    // emit('input', { id: props.inputId, value });
};

const onItemSelect = (event) => {
    ValidationMsg.value = '';
    const selectedValue = event.value.value;
    const selectedLabel = event.value.label || selectedValue;
    inputValue.value = selectedLabel;

    emit('input', { id: props.inputId, value: selectedValue });
    // v-model 업데이트
    emit('update:modelValue', selectedValue);
};

onMounted(() => {
    // 초기값 설정
    if (props.modelValue === '' && items.value.length > 0) {
        // selectAll 옵션이 있으면 그것을 선택, 없으면 첫 번째 옵션 선택
        selectedValue.value = props.selectAll ? '' : items.value[0].value;
        emit('update:modelValue', selectedValue.value);
    }
});

const clearInput = () => {
    inputValue.value = '';
    emit('input', { id: props.inputId, value: '' });
    emit('update:modelValue', '');

    // clear시 오토컴플릿 팝업창 유지 테스트
    // search({ query: '' });
    // nextTick(() => {
    //     const inputElement = autocompleteRef.value.$el.querySelector('p-autocomplete-option');
    //     inputElement.focus();
    //     if (autocompleteRef.value) {
    //         autocompleteRef.value.show();
    //     }
    // });
};

const getValueForTitle = (label) => {
    const item = items.value.find((item) => item.label === label);
    return item ? item.value : '';
};
</script>

<template>
    <div v-if="visible" class="flex flex-col" :style="{ marginTop, marginBottom, marginLeft, marginRight }">
        <label :class="['label-font mb-1 mx-1', required ? 'label-required' : 'text-gray-700 dark:text-gray-300']">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>
        <!-- AutoComplete 컴포넌트 -->
        <div class="relative w-full">
            <AutoComplete
                ref="autocompleteRef"
                optionLabel="label"
                v-model="inputValue"
                :suggestions="items"
                dropdown
                :disabled="isReadonly"
                :title="getValueForTitle(inputValue)"
                :placeholder="placeholder"
                @complete="search"
                @item-select="onItemSelect"
                @input="onInput"
                :class="['custom-autocomplete w-full', disabled ? 'disabled' : '', { 'error-border': ValidationMsg }]"
            >
                <template #input="slotProps">
                    <input
                        v-bind="slotProps.inputProps"
                        @click="search({ query: inputValue.value.trim() })"
                        :id="inputId"
                        :class="['block w-full p-3 ps-10 text-sm text-gray-900 bg-transparent rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-0']"
                    />
                </template>
            </AutoComplete>
            <button v-if="inputValue && !isReadonly" @click="clearInput" class="clear-button">&times;</button>
        </div>

        <!-- <span class="select-icon pi pi-calendar" @click="openSelectBox"></span> -->
        <!-- 드롭다운 아이콘 -->
        <!-- <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none dropdown-icon-wrapper">
            <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
        </div> -->
    </div>
</template>

<style scoped>
.label-font {
    font-family: 'SCoreDream600';
    height: 1.3rem;
    /* font-size: 0.875rem; */
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.error-border {
    border: 0.1rem solid red !important;
    border-radius: 0.5rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.custom-autocomplete :deep(.p-autocomplete) {
    width: 100%;
    border-radius: 0.5rem;
    overflow: hidden;
}

.custom-autocomplete :deep(.p-inputtext) {
    border-radius: 0.5rem !important;
    padding-right: 4rem;
    border: 1px solid #d1d5db;
    background-color: white;
}

.custom-autocomplete :deep(.p-autocomplete-input) {
    height: 3rem;
    font-size: 0.875rem !important;
    font-family: 'SCoreDream400' !important;
}

/* 포커스 색상 */
.custom-autocomplete :deep(.p-autocomplete-input:focus) {
    border-color: #0038ea; /* 포커스 시 테두리 색상 */
    outline: none; /* 포커스 시 기본 외곽선 제거 */
}

.custom-autocomplete :deep(.p-inputtext:disabled) {
    border: 1px solid darkgray;
    color: black;
}

.custom-autocomplete :deep(.p-autocomplete-dropdown) {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
}

.custom-autocomplete :deep(.p-autocomplete-item) {
    cursor: pointer;
}

/* 선택된 항목의 배경색 */
.custom-autocomplete :deep(.p-autocomplete-item.p-highlight) {
    background-color: #f1f5f9 !important; /* 선택한 항목의 배경을 회색으로 설정 */
}

.custom-autocomplete :deep(.p-autocomplete-dropdown:hover) {
    background-color: transparent;
}

.clear-button {
    position: absolute;
    right: 2.5rem; /* AutoComplete의 드롭다운 아이콘을 고려한 위치 */
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    width: 1.6rem;
    height: 1.6rem;
    font-family: 'SCoreDream400';
    font-size: 1.4rem;
    background: transparent;
    border: none;
    opacity: 0.5;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1; /* AutoComplete 컴포넌트 위에 표시되도록 */
}

.clear-button:hover {
    font-family: 'SCoreDream500';
    opacity: 1;
}
</style>
